/**
 * CheckoutProgress.tsx — step bar across checkout (plan → delivery & payment → account).
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * DELIVERY AND PAYMENT ARE ONE STEP (CTO, 2026-09-23): after the plan, the
 * patient enters the address and the card in ONE continuous section, so the bar
 * lists one step for both. Listing them separately here while the screen merges
 * them would tell the patient there is a step change that never comes.
 *
 * Guest funnel pays before account creation; signed-in patients skip Account
 * (progress still lists the two money steps). Confirm is the result, not a step.
 *
 * Accessibility: ordered list in <nav>, aria-current="step", sr-only "Step N of M".
 *
 * ONE WIDTH ON EVERY STEP (journey polish, 2026-09-24). The bar is capped at the
 * classic 32rem column and centred, so it is the same 512px on the plan screen,
 * the delivery & payment step (whose two-column shell is 64rem wide) and the
 * account step — it used to jump to the full 1024px band on delivery only.
 *
 * ON A PHONE THE LABELS WRAP (journey polish #1). Three nowrap labels cannot
 * share 343px: "Delivery & payment" ran into the next circle and "Account" off
 * the screen. The `gps-jux-step*` class hooks let tokens.css (P7, journey-scoped)
 * let a label wrap onto two short lines beside its circle below 560px. The
 * inline styles here are the desktop look and are unchanged.
 */

export type CheckoutStep = 'plan' | 'details' | 'account';

const STEPS_GUEST: { key: CheckoutStep; label: string }[] = [
  { key: 'plan', label: 'Your plan' },
  { key: 'details', label: 'Delivery & payment' },
  { key: 'account', label: 'Account' },
];

const STEPS_SIGNED_IN: { key: CheckoutStep; label: string }[] = [
  { key: 'plan', label: 'Your plan' },
  { key: 'details', label: 'Delivery & payment' },
];

export function CheckoutProgress({
  current,
  includeAccount = false,
}: {
  current: CheckoutStep;
  /** Guest post-pay account step — omit when already signed in. */
  includeAccount?: boolean;
}) {
  const STEPS = includeAccount ? STEPS_GUEST : STEPS_SIGNED_IN;
  const currentIndex = Math.max(
    0,
    STEPS.findIndex((s) => s.key === current),
  );

  return (
    <nav
      aria-label="Checkout progress"
      className="gps-jux-stepper"
      style={{ maxWidth: '32rem', margin: '0 auto var(--gps-s-5)' }}
    >
      <p className="gps-sr-only">
        Step {currentIndex + 1} of {STEPS.length}
      </p>
      <ol
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          listStyle: 'none',
          margin: 0,
          padding: 0,
        }}
      >
        {STEPS.map((step, i) => {
          const done = i < currentIndex;
          const active = i === currentIndex;
          return (
            <li
              key={step.key}
              aria-current={active ? 'step' : undefined}
              className="gps-jux-step"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: i === STEPS.length - 1 ? '0 1 auto' : '1 1 auto',
                minWidth: 0,
              }}
            >
              <span
                aria-hidden="true"
                className="gps-jux-step-mark"
                data-state={done ? 'done' : active ? 'current' : 'upcoming'}
                style={{
                  width: '26px',
                  height: '26px',
                  flex: 'none',
                  boxSizing: 'border-box',
                  borderRadius: 'var(--gps-r-pill)',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: '13px',
                  fontWeight: 700,
                  background: done || active ? 'var(--gps-primary)' : 'var(--gps-surface-2)',
                  color: done || active ? '#fff' : 'var(--gps-muted)',
                  border: done || active ? '1px solid var(--gps-primary)' : '1px solid var(--gps-border)',
                }}
              >
                {done ? '✓' : i + 1}
              </span>
              <span
                className="gps-jux-step-label"
                style={{
                  fontSize: '14px',
                  fontWeight: active ? 700 : 500,
                  color: active ? 'var(--gps-text)' : 'var(--gps-muted)',
                  whiteSpace: 'nowrap',
                }}
              >
                {step.label}
                {done ? <span className="gps-sr-only"> — completed</span> : null}
              </span>
              {i < STEPS.length - 1 ? (
                <span
                  aria-hidden="true"
                  className="gps-jux-step-line"
                  style={{
                    flex: '1 1 auto',
                    height: '2px',
                    minWidth: '12px',
                    borderRadius: '2px',
                    background: done ? 'var(--gps-primary)' : 'var(--gps-border)',
                  }}
                />
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
